import { State } from '../types'
import { State as SettingsState } from './reducer'

import { StickyColumnGrid, WatchlistWindowSetting } from './types'

const DEFAULT_PREFIX = 'mts.'
export interface Setting {
  hasValue: boolean
  value?: any
}

export type Settings = Record<string, Record<string, Setting>>

export const injectSettingsIntoState = (
  settings: Settings,
  state: Record<string, Record<string, any>>
) => {
  return Object.entries(state).reduce(
    (globalState, [namespace, subState]) => ({
      ...globalState,
      [namespace]: settings[namespace]
        ? Object.entries(settings[namespace]).reduce(
            (acc, [key, setting]) =>
              setting.hasValue ? { ...acc, [key]: setting.value } : acc,
            subState
          )
        : subState
    }),
    state
  )
}

export const injectWindowSettingsIntoState = (
  settings: WatchlistWindowSetting[],
  stickyColumnGrids: Record<
    KeysWithValsOfType<SettingsState, StickyColumnGrid>,
    StickyColumnGrid | undefined
  >,
  mutedTradingNowAlerts: any[],
  mutedCounteredAlerts: any[],
  mutedToppedAlerts: any[],
  DepthSecurityIds: number[],
  maxDOMs: number,
  state: State
): State => {
  const { auth, ...otherState } = state
  const tempState = JSON.parse(
    JSON.stringify({ ...otherState, auth: { ...auth, oktaAuth: undefined } })
  )
  tempState.auth.oktaAuth = auth.oktaAuth
  tempState.alerts.mutedAlerts.tradingNow = mutedTradingNowAlerts
  tempState.alerts.mutedAlerts.countered = mutedCounteredAlerts
  tempState.alerts.mutedAlerts.topped = mutedToppedAlerts
  settings.forEach((a, i) => {
    tempState.grid.columnsOrder[i] = a.columnDefinitions
    tempState.order.myOrdersIsOpen[i] = a.myOrdersIsOpen
    tempState.securities.gridDefinitions[i] = {
      watchlistId: a.watchlistId ?? undefined,
      isMine: a.isMine,
      myFirm: a.myFirm,
      myBook: a.myBook,
      showLive: a.showLive,
      quoteReliability: a.qr,
      order: 0,
      filter: a.filter,
      useSize: a.useSize,
      size: a.size,
      isMineWatchlist: a.isMineWatchlist,
      useAdvancedFilter: a.useAdvancedFilter
    }
  })
  tempState.settings = {
    ...tempState.settings,
    areSettingsLoaded: true,
    ...stickyColumnGrids
  }
  tempState.depthofmarket.popoutSecurityIds = DepthSecurityIds ?? []
  tempState.depthofmarket.maxDOMs = maxDOMs
  return tempState
}

export const settingsObject = (state: { value: State }) => {
  const arr = []
  for (const property in state.value.securities.gridDefinitions) {
    if (state.value.securities.gridDefinitions.hasOwnProperty(property)) {
      const val = state.value.securities.gridDefinitions[property]
      const watchlistWindowSetting: WatchlistWindowSetting = {
        watchlistId: val.watchlistId === undefined ? null : val.watchlistId,
        showLive: val.showLive,
        myFirm: val.myFirm,
        myBook: val.myBook,
        qr: val.quoteReliability,
        isMine: val.isMine,
        myOrdersIsOpen: state.value.order.myOrdersIsOpen[property],
        columnDefinitions: state.value.grid.columnsOrder[property],
        filter: val.filter,
        useSize: val.useSize,
        size: val.size,
        isMineWatchlist: val.isMineWatchlist,
        useAdvancedFilter: val.useAdvancedFilter
      }
      arr.push(watchlistWindowSetting)
    }
  }

  const s = {
    watchlistWindowSettings: arr,
    aggressAttemptWindowSettings: state.value.settings.aggressAttemptSettings,
    externalOrderWindowSettings: state.value.settings.externalOrderSettings,
    externalLiquidityWindowSettings:
      state.value.settings.externalLiquiditySettings,
    historicalDataWindowSettings: state.value.settings.historicalDataSettings,
    listTradingWindowSettings: state.value.settings.listTradingSettings,
    mutedTradingNowAlerts: state.value.alerts.mutedAlerts.tradingNow,
    mutedCounteredAlerts: state.value.alerts.mutedAlerts.countered,
    mutedToppedAlerts: state.value.alerts.mutedAlerts.topped,
    DepthSecurityIds: state.value.depthofmarket.popoutSecurityIds,
    maxDOMs: state.value.depthofmarket.maxDOMs
  }
  return s
}

export const defaultBackendSettings = {
  watchlistWindowSettings: [],
  aggressAttemptWindowSettings: null,
  externalOrderWindowSettings: null,
  externalLiquidityWindowSettings: null,
  historicalDataWindowSettings: null,
  mutedTradingNowAlerts: [],
  mutedCounteredAlerts: [],
  mutedToppedAlerts: [],
  DepthSecurityIds: [],
  maxDOMs: 20
}

export const clearStorage = (valuesToSave: string[]) => {
  valuesToSave
    .map((key) => `${DEFAULT_PREFIX}${key}`)
    .forEach((key) => localStorage.removeItem(key))
}
